import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum WorkOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const workOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  vendor: yup.object().shape({
    attn: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
})

export type WorkOrderFileData = yup.InferType<typeof workOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<WorkOrderFileData>

export const WorkOrderFileMappings: RequiredFileData = {
  issuer: {
    attn: '47ece162-a135-4230-9e18-db0c1a649cca',
    companyName: 'cb6f329c-f6f4-4c6e-9c82-ddc00d94cd9f',
    addressLine1: '0da4b803-8285-4104-a8f6-3456af0ab27d',
    addressLine2: '1b45055e-3e68-4f31-9b53-d8b5021c2c71',
    city: '9ee5f19f-1eba-48f4-94fd-f763839647e3',
    state: '974ce774-7ff2-4d5a-9b18-771715d5cae5',
    zipCode: 'f741a79c-75fc-4c56-baf6-a9d768248172',
    country: '447386ac-e8a5-46c1-be0e-fc2f3ec4b06d',
    phoneNumber: '673abe1b-c027-4124-87f4-8d4760a91db5',
    emailAddress: 'd1cbfd32-be32-4ee7-8996-616a495638ce',
  },
  orderNumber: 'dc74b8f2-f139-42be-8b88-36397ec18df6',
  paymentTerms: 'f94735ab-d4bc-4b48-927a-5829cda008c6',
  freightTerms: '76373fa7-04e7-4c62-a2c0-e57c3244ac69',
  dateIssued: 'c092cb0b-567b-4680-9996-85d0f578b8b1',
  dateReady: 'e24ea30b-c8e6-4a88-8122-4997e32444b6',
  destination: {
    attn: '48f40334-179a-450c-b31b-a5e063509677',
    companyName: 'eaeb88a1-9eb4-49b6-9be7-fce5d6088caf',
    addressLine1: 'f36a2b8c-e6b4-46a4-9dab-66c386f3374e',
    addressLine2: 'a6ffa028-d8f6-48b1-b2ab-43b022e72cae',
    city: 'cc342beb-b7b4-4980-ab20-6c05a602c28b',
    state: 'fd0b3382-642c-489b-b19b-1477df8c5248',
    zipCode: '1e01f751-eb92-4296-b479-043383c30912',
    country: 'ef37d08e-7bd5-4290-811b-04ab4d778578',
    phoneNumber: '4e089590-e2ef-4a80-8a2b-ada825924f0c',
    emailAddress: '6c27337b-eefb-4233-ae3e-20f5f489fa4e',
  },
  vendor: {
    attn: '2a96b6fa-f271-4a5b-9506-cb5e0c1a350a',
    companyName: 'a88f42eb-6646-48a2-875c-8f634c2acbd2',
    addressLine1: '526e654e-8ccf-47bc-a2f4-efcd11c39e6f',
    addressLine2: 'f936d544-b0fb-4655-8e96-7c558a11be69',
    city: 'c4fd4f90-31b0-4327-8f68-a4704fba42df',
    state: 'b20ba674-05b0-4a10-8b2a-5cf1cd251622',
    zipCode: '08bc9481-9b62-484e-bedb-33e3e63513dd',
    country: '9daa5dfd-2baf-4256-af84-ac18dce934da',
    phoneNumber: '8ea0cb09-2836-4213-8d13-e16e4ebbf3cd',
    emailAddress: '4d9641d0-871f-4e86-b0ba-8da7b5a18aa8',
  },
  items: [
    {
      itemNumber: null,
      itemName: '330c8530-6b15-4214-93bf-483fdf85407a',
      description: 'd6190676-8cff-4126-b3ad-3295eabe661e',
      itemQty: '6e7e2ae9-cf9e-4009-aa55-8bfd3f857aba',
      unitPrice: '457b4594-5720-4a0a-a197-043691f7695c',
      lineTotal: null,
    },
  ],
  agreement: 'bd820144-7ae7-4b5f-9e5f-42ada4b35e6e',
  instructions: '41aecc4d-c769-4e80-af28-227ddb7e57ad',
}
